import Image from "next/image"
import Link from "next/link"
import { ArrowRight, Check, Minus, Sparkles, TrendingUp } from "lucide-react"

import { MotionDiv, MotionList, MotionSection, blurIn, fadeInLeft, scaleIn } from "@/components/motion"
import { AmbientGlow, LiftCard } from "@/components/motion-primitives"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"

export type LogoCloudItem = {
  name: string
  image?: { src: string; alt: string }
}

export type LogoCloudSectionProps = {
  eyebrow?: string
  title?: string
  description?: string
  logos: LogoCloudItem[]
  className?: string
}

export type ComparisonRow = {
  feature: string
  us: string | boolean
  them?: string | boolean
}

export type ComparisonMatrixSectionProps = {
  eyebrow?: string
  title: string
  description?: string
  rows: ComparisonRow[]
  leftLabel?: string
  rightLabel?: string
  cta?: { label: string; href: string }
  className?: string
}

export type MetricShowcaseItem = {
  value: string
  label: string
  description?: string
}

export type MetricsShowcaseSectionProps = {
  eyebrow?: string
  title: string
  description?: string
  metrics: MetricShowcaseItem[]
  quote?: { body: string; author: string; role?: string }
  className?: string
}

export function LogoCloudSection({ eyebrow = "Trusted by teams with taste", title, description, logos, className }: LogoCloudSectionProps) {
  const repeatedLogos = [...logos, ...logos]

  return (
    <MotionSection className={cn("overflow-hidden border-y border-border/70 bg-background py-16 sm:py-20", className)}>
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv variants={fadeInLeft} className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary">{eyebrow}</p>
          {title && <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-5xl">{title}</h2>}
          {description && <p className="mt-4 text-base leading-7 text-muted-foreground">{description}</p>}
        </MotionDiv>
      </div>
      <div className="mt-10 flex overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
        <div className="flex min-w-full shrink-0 animate-marquee items-center gap-4 pr-4 motion-reduce:animate-none">
          {repeatedLogos.map((logo, index) => (
            <div key={`${logo.name}-${index}`} className="flex h-20 min-w-48 items-center justify-center rounded-card border border-border/70 bg-card px-6 text-center shadow-sm">
              {logo.image ? (
                <Image src={logo.image.src} alt={logo.image.alt} width={140} height={44} className="max-h-10 w-auto object-contain" />
              ) : (
                <span className="font-heading text-lg font-semibold tracking-tight text-foreground/78">{logo.name}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </MotionSection>
  )
}

function comparisonValue(value: string | boolean | undefined, highlighted: boolean) {
  if (typeof value === "boolean") {
    return value ? <Check className={cn("mx-auto size-5", highlighted ? "text-primary" : "text-muted-foreground")} /> : <Minus className="mx-auto size-5 text-muted-foreground/60" />
  }
  return <span className={cn("text-sm font-medium", highlighted ? "text-foreground" : "text-muted-foreground")}>{value ?? "-"}</span>
}

export function ComparisonMatrixSection({ eyebrow, title, description, rows, leftLabel = "Hiveku-ready", rightLabel = "Generic builder", cta, className }: ComparisonMatrixSectionProps) {
  return (
    <MotionSection className={cn("bg-muted/42 py-24 sm:py-32", className)}>
      <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.78fr_1.22fr] lg:px-8">
        <MotionDiv variants={fadeInLeft} className="lg:sticky lg:top-24 lg:self-start">
          {eyebrow && <Badge variant="secondary" className="mb-5 rounded-full px-4 py-2 text-primary">{eyebrow}</Badge>}
          <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-6xl">{title}</h2>
          {description && <p className="mt-5 text-lg leading-8 text-muted-foreground">{description}</p>}
          {cta && (
            <Button asChild size="lg" className="mt-8 rounded-full">
              <Link href={cta.href}>{cta.label}<ArrowRight className="size-4" /></Link>
            </Button>
          )}
        </MotionDiv>
        <MotionDiv variants={blurIn}>
          <Card className="overflow-hidden rounded-panel border-border/70 bg-card shadow-[var(--shadow-card)]">
            <div className="grid grid-cols-[1.2fr_0.9fr_0.9fr] border-b border-border/70 bg-background/80 text-sm font-semibold text-foreground">
              <div className="p-4">Capability</div>
              <div className="border-l border-border/70 bg-primary/8 p-4 text-primary">{leftLabel}</div>
              <div className="border-l border-border/70 p-4 text-muted-foreground">{rightLabel}</div>
            </div>
            {rows.map((row) => (
              <div key={row.feature} className="grid grid-cols-[1.2fr_0.9fr_0.9fr] border-b border-border/70 last:border-0">
                <div className="p-4 text-sm font-medium text-foreground">{row.feature}</div>
                <div className="border-l border-border/70 bg-primary/5 p-4 text-center">{comparisonValue(row.us, true)}</div>
                <div className="border-l border-border/70 p-4 text-center">{comparisonValue(row.them, false)}</div>
              </div>
            ))}
          </Card>
        </MotionDiv>
      </div>
    </MotionSection>
  )
}

export function MetricsShowcaseSection({ eyebrow, title, description, metrics, quote, className }: MetricsShowcaseSectionProps) {
  return (
    <MotionSection className={cn("relative isolate overflow-hidden bg-foreground py-24 text-background sm:py-32", className)}>
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_18%_20%,hsl(var(--primary)/0.26),transparent_30%),radial-gradient(circle_at_86%_72%,hsl(var(--accent)/0.18),transparent_28%)]" />
      <AmbientGlow className="-right-32 top-1/3 h-[24rem] w-[24rem] bg-accent/14" duration={8} />
      <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.92fr_1.08fr] lg:px-8">
        <MotionDiv variants={fadeInLeft}>
          {eyebrow && <p className="mb-5 text-sm font-semibold uppercase tracking-[0.22em] text-accent">{eyebrow}</p>}
          <h2 className="font-heading text-4xl font-semibold tracking-tight sm:text-6xl">{title}</h2>
          {description && <p className="mt-5 text-lg leading-8 text-background/70">{description}</p>}
          {quote && (
            <blockquote className="mt-10 rounded-panel border border-background/10 bg-background/10 p-6 backdrop-blur-xl">
              <Sparkles className="mb-5 size-5 text-accent" />
              <p className="text-xl leading-8 text-background/86">{quote.body}</p>
              <footer className="mt-5 text-sm text-background/62"><span className="font-semibold text-background">{quote.author}</span>{quote.role ? `, ${quote.role}` : ""}</footer>
            </blockquote>
          )}
        </MotionDiv>
        <MotionList className="grid gap-4 sm:grid-cols-2">
          {metrics.map((metric, index) => (
            <MotionDiv key={metric.label} variants={scaleIn} className={cn(index === 0 && "sm:col-span-2")}>
              <LiftCard lift={6} scale={1.02}>
                <div className="h-full rounded-panel border border-background/10 bg-background/10 p-6 backdrop-blur-xl">
                  <div className="mb-8 flex size-12 items-center justify-center rounded-card bg-accent text-accent-foreground"><TrendingUp className="size-5" /></div>
                  <p className="font-heading text-5xl font-semibold tracking-tight sm:text-6xl">{metric.value}</p>
                  <p className="mt-3 font-semibold text-background">{metric.label}</p>
                  {metric.description && <p className="mt-3 text-sm leading-6 text-background/64">{metric.description}</p>}
                </div>
              </LiftCard>
            </MotionDiv>
          ))}
        </MotionList>
      </div>
    </MotionSection>
  )
}
